import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Clock, User, Dog, Cat, Phone, Mail, AlertTriangle, Calendar } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
  color?: string;
  gender?: string;
  weight?: number;
  date_of_birth?: string;
  behavioral_tags?: {
    aggressive?: boolean;
    fearful?: boolean;
    go_slow?: boolean;
    caution?: boolean;
    demo?: boolean;
    notes?: string;
  };
}

interface Owner {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  secondary_phone?: string;
  address?: string;
}

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  duration_minutes: number;
  appointment_type: string;
  status: string;
  description?: string;
  notes?: string;
  pets?: Pet[];
  pet_owner?: Owner;
}

interface EnhancedAppointmentModalProps {
  appointment: Appointment;
  onClose: () => void;
  onSave: (updatedAppointment: Appointment) => Promise<void>;
  onDelete?: (appointmentId: string) => Promise<void>;
  onApprove?: (appointmentId: string) => Promise<void>;
  onReject?: (appointmentId: string, reason?: string) => Promise<void>;
}

const BEHAVIORAL_TAGS = [
  { key: 'aggressive', label: 'Aggressive', color: 'bg-orange-100 text-orange-800 border-orange-400 border-2' },
  { key: 'fearful', label: 'Fearful', color: 'bg-blue-100 text-blue-800 border-blue-400 border-2' },
  { key: 'go_slow', label: 'Go Slow', color: 'bg-green-100 text-green-800 border-green-400 border-2' },
  { key: 'caution', label: 'Caution', color: 'bg-orange-100 text-orange-800 border-orange-400 border-2' },
  { key: 'demo', label: 'Demo', color: 'bg-blue-100 text-blue-800 border-blue-400 border-2' },
];

export const EnhancedAppointmentModal: React.FC<EnhancedAppointmentModalProps> = ({
  appointment,
  onClose,
  onSave,
  onDelete,
  onApprove,
  onReject,
}) => {
  const [editedAppointment, setEditedAppointment] = useState<Appointment>(appointment);
  const [editedPets, setEditedPets] = useState<Pet[]>(appointment.pets || []);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'pet' | 'visit'>('pet');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const isPending = appointment.status?.toLowerCase() === 'pending';

  const handleApprove = async () => {
    if (!onApprove) return;
    setApproving(true);
    try {
      await onApprove(appointment.id);
      onClose();
    } catch (error) {
      console.error('Failed to approve appointment:', error);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!onReject) return;
    const reason = window.prompt('Reason for rejection (optional):');
    setRejecting(true);
    try {
      await onReject(appointment.id, reason || undefined);
      onClose();
    } catch (error) {
      console.error('Failed to reject appointment:', error);
    } finally {
      setRejecting(false);
    }
  };

  useEscapeKey(onClose, true);

  useEffect(() => {
    setEditedAppointment(appointment);
    setEditedPets(appointment.pets || []);
  }, [appointment]);

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const endDate = new Date(date.getTime() + editedAppointment.duration_minutes * 60000);
      const timeFormat: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      };
      return `${date.toLocaleTimeString('en-US', timeFormat)} – ${endDate.toLocaleTimeString('en-US', timeFormat)}`;
    } catch {
      return 'Time not available';
    }
  };

  const calculateAge = (dateOfBirth?: string) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birth = new Date(dateOfBirth);
    const years = today.getFullYear() - birth.getFullYear();
    const months = today.getMonth() - birth.getMonth();
    
    if (years > 0) {
      if (months < 0) {
        return `${years - 1} year${years - 1 !== 1 ? 's' : ''} ${12 + months} month${12 + months !== 1 ? 's' : ''}`;
      }
      return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
    }
    return `${months} month${months !== 1 ? 's' : ''}`;
  };

  const toggleBehavioralTag = (petId: string, tagKey: string) => {
    setEditedPets(pets =>
      pets.map(pet => {
        if (pet.id !== petId) return pet;
        const currentTags = pet.behavioral_tags || {};
        return {
          ...pet,
          behavioral_tags: {
            ...currentTags,
            [tagKey]: !currentTags[tagKey as keyof typeof currentTags],
          },
        };
      })
    );
  };

  const updatePetNotes = (petId: string, notes: string) => {
    setEditedPets(pets =>
      pets.map(pet => {
        if (pet.id !== petId) return pet;
        return {
          ...pet,
          behavioral_tags: {
            ...pet.behavioral_tags,
            notes,
          },
        };
      })
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Update each pet's behavioral tags
      for (const pet of editedPets) {
        await fetch(`${API_BASE_URL}/api/v1/pets/${pet.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            behavioral_tags: pet.behavioral_tags || {},
          }),
        });
      }

      // Update appointment (description, notes, title, appointment_type)
      await fetch(`${API_BASE_URL}/api/v1/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: editedAppointment.title,
          description: editedAppointment.description,
          notes: editedAppointment.notes,
          appointment_type: editedAppointment.appointment_type,
        }),
      });

      // Update appointment
      const updatedAppointment = {
        ...editedAppointment,
        pets: editedPets,
      };
      await onSave(updatedAppointment);
      onClose();
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAppointmentTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'checkup':
        return 'bg-emerald-400 border-emerald-500 hover:bg-emerald-500';
      case 'emergency':
        return 'bg-orange-400 border-orange-500 hover:bg-orange-500';
      case 'surgery':
        return 'bg-blue-500 border-blue-600 hover:bg-blue-600';
      case 'consultation':
        return 'bg-blue-400 border-blue-500 hover:bg-blue-500';
      default:
        return 'bg-gray-400 border-gray-500 hover:bg-gray-500';
    }
  };

  const getPetIcon = (species: string) => {
    const lowerSpecies = species?.toLowerCase();
    if (lowerSpecies === 'dog' || lowerSpecies === 'canine') {
      return <Dog className="w-5 h-5" />;
    } else if (lowerSpecies === 'cat' || lowerSpecies === 'feline') {
      return <Cat className="w-5 h-5" />;
    }
    return <span className="text-lg">🐾</span>;
  };

  const owner = appointment.pet_owner;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[500] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-semibold text-gray-900 mb-1">
                {editedAppointment.title}
              </h2>
              <p className="text-gray-600 text-sm">
                {formatDateTime(editedAppointment.appointment_date)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {onDelete && (
              <button
                onClick={() => onDelete(appointment.id)}
                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete appointment"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Pending Approval Banner */}
        {isPending && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-amber-800">
                  Pending Approval - Online Booking Request
                </span>
              </div>
              {onApprove && onReject && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleReject}
                    disabled={rejecting}
                    className="px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    {rejecting ? 'Rejecting...' : 'Reject'}
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {approving ? 'Approving...' : 'Approve'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Appointment Info Bar */}
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted">Time</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    {formatTime(editedAppointment.appointment_date)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                    {editedAppointment.duration_minutes} minutes
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={editedAppointment.appointment_type}
                  onChange={(e) => setEditedAppointment({ ...editedAppointment, appointment_type: e.target.value })}
                  className={`text-xs px-2 py-1 rounded border text-white font-medium cursor-pointer transition-colors appearance-none pr-6 ${getAppointmentTypeColor(editedAppointment.appointment_type)}`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 0.25rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1rem 1rem'
                  }}
                >
                  <option value="checkup" className="bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary">Checkup</option>
                  <option value="emergency" className="bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary">Emergency</option>
                  <option value="surgery" className="bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary">Surgery</option>
                  <option value="consultation" className="bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary">Consultation</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-dark-border">
            <div className="flex">
              <button
                onClick={() => setActiveTab('pet')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'pet'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
                }`}
              >
                Pet & Owner Info
              </button>
              <button
                onClick={() => setActiveTab('visit')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'visit'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary'
                }`}
              >
                Visit Details
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'pet' ? (
              <div className="space-y-6">
                {/* Pet Information */}
                {editedPets.map((pet) => (
                  <div key={pet.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 flex-shrink-0">
                        {getPetIcon(pet.species)}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{pet.name}</h3>
                        <p className="text-sm text-gray-600">
                          {pet.species} • {pet.breed || 'Mixed'} • {pet.gender || 'Unknown'}
                        </p>
                        {pet.date_of_birth && (
                          <p className="text-sm text-gray-500">
                            {calculateAge(pet.date_of_birth)} old
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Behavioral Tags */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Behavioral Tags
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {BEHAVIORAL_TAGS.map((tag) => {
                          const isActive = pet.behavioral_tags?.[tag.key as keyof typeof pet.behavioral_tags];
                          return (
                            <button
                              key={tag.key}
                              onClick={() => toggleBehavioralTag(pet.id, tag.key)}
                              className={`px-4 py-2.5 text-sm font-bold rounded-lg transition-all ${
                                isActive
                                  ? tag.color
                                  : 'bg-white text-gray-500 border-2 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {tag.label.toUpperCase()}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Behavioral Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Special Instructions
                      </label>
                      <textarea
                        value={pet.behavioral_tags?.notes || ''}
                        onChange={(e) => updatePetNotes(pet.id, e.target.value)}
                        placeholder="Add any special handling instructions or notes..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                      />
                    </div>
                  </div>
                ))}

                {/* Owner Information */}
                {owner && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <User className="w-5 h-5 text-gray-400" />
                      Owner Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{owner.full_name}</span>
                      </div>
                      {owner.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <a href={`tel:${owner.phone}`} className="text-blue-600 hover:underline">
                            {owner.phone}
                          </a>
                        </div>
                      )}
                      {owner.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a href={`mailto:${owner.email}`} className="text-blue-600 hover:underline">
                            {owner.email}
                          </a>
                        </div>
                      )}
                      {owner.secondary_phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Secondary: {owner.secondary_phone}</span>
                        </div>
                      )}
                      {owner.address && (
                        <div className="text-sm text-gray-600 ml-6">
                          {owner.address}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Visit Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Appointment Title
                  </label>
                  <input
                    type="text"
                    value={editedAppointment.title}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editedAppointment.description || ''}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, description: e.target.value })}
                    placeholder="Reason for visit, symptoms, etc..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Internal Notes
                  </label>
                  <textarea
                    value={editedAppointment.notes || ''}
                    onChange={(e) => setEditedAppointment({ ...editedAppointment, notes: e.target.value })}
                    placeholder="Staff notes, follow-up items, etc..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

